import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { alpha, useTheme } from '@mui/material/styles';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  arrayMove,
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { formatEquityFieldValue } from 'util/equityhub';
import { EquityFieldKey, LegacyEquity } from 'types';
import { EQUITY_FIELD_LABEL_MAPPING } from 'config/equityhub';
import { useRecoilState, useRecoilValue } from 'recoil';
import { OPTIONS_CALCULATOR_ALL_FIELDS } from 'config/optionsCalculator';
import { formatAsCurrency, formatAsPercentage } from 'util/shared';
import { optionsCalculatorTickerCurrentFieldsState } from 'states/optionsCalculator';
import useOptionsCalculator from 'hooks/optionsCalculator/useOptionsCalculator';
import usePrices from 'hooks/equityhub/usePrices';
import { useQuery } from '@tanstack/react-query';
import { unpricedEquitiesState } from 'states/equityhub';

interface TickerPillProps {
  id: string;
  field: EquityFieldKey;
  value: number | string | boolean | undefined;
  onDelete: () => void;
}

const TickerPill: React.FC<TickerPillProps> = ({
  id,
  field,
  value,
  onDelete,
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    transition,
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onDelete();
  };

  return (
    <Chip
      ref={setNodeRef}
      style={style}
      label={
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontSize: {
                xs: 10,
                md: 12,
              },
            }}
          >
            {EQUITY_FIELD_LABEL_MAPPING[field]}:
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              fontSize: {
                xs: 12,
                md: 14,
              },
            }}
          >
            {formatEquityFieldValue(field, value)}
          </Typography>
        </Stack>
      }
      onDelete={handleDelete}
      deleteIcon={<CloseIcon fontSize="small" />}
      sx={{
        m: 0.5,
        p: 1,
        height: 'auto',
        cursor: 'grab',
        backgroundColor: alpha(theme.palette.primary.main, 0.25),
        '&:hover': {
          backgroundColor: alpha(theme.palette.primary.main, 0.35),
        },
      }}
      {...attributes}
      {...listeners}
    />
  );
};

const TickerOverview = () => {
  const theme = useTheme();
  const { sym } = useOptionsCalculator();
  const unpricedEquities = useRecoilValue(unpricedEquitiesState);
  const [displayedFields, setDisplayedFields] = useRecoilState(
    optionsCalculatorTickerCurrentFieldsState,
  );
  const [openModal, setOpenModal] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [hiddenFields, setHiddenFields] = useState<EquityFieldKey[]>(() =>
    OPTIONS_CALCULATOR_ALL_FIELDS.filter(
      (field) => !displayedFields.includes(field),
    ),
  );

  const { getPrices } = usePrices();

  // Use React Query to fetch prices
  const { data: pricePayload } = useQuery<Map<string, number>>({
    queryKey: ['prices', sym],
    queryFn: async () => {
      if (!sym) {
        return new Map<string, number>();
      }
      return getPrices([]);
    },
    enabled: !!sym,
    refetchOnWindowFocus: false,
  });

  const pricedEquity = useMemo(() => {
    const e = unpricedEquities.get(sym);
    if (e == null) {
      return null;
    }
    const price = pricePayload?.get(e.sym) ?? e.upx;
    return {
      ...e,
      [EquityFieldKey.price]: price,
      [EquityFieldKey.dailyChange]:
        price && e.upx ? (price - e.upx) / e.upx : undefined,
      [EquityFieldKey.prevClose]: e.upx ? e.upx : undefined,
    } as LegacyEquity;
  }, [unpricedEquities, pricePayload, sym]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setDisplayedFields((fields) => {
        const oldIndex = fields.indexOf(active.id as EquityFieldKey);
        const newIndex = fields.indexOf(over.id as EquityFieldKey);
        return arrayMove(fields, oldIndex, newIndex);
      });
    }

    setActiveId(null);
  };

  const handleRemoveField = (field: EquityFieldKey) => {
    setDisplayedFields((fields) => fields.filter((f) => f !== field));
    setHiddenFields((fields) => [...fields, field]);
  };

  const handleAddField = (field: EquityFieldKey) => {
    setHiddenFields((fields) => fields.filter((f) => f !== field));
    setDisplayedFields((fields) => [...fields, field]);
  };

  const isPriceUp = (pricedEquity?.[EquityFieldKey.dailyChange] ?? 0) >= 0;
  const priceChange =
    (pricedEquity?.[EquityFieldKey.price] ?? 0) -
      (pricedEquity?.[EquityFieldKey.prevClose] ?? 0) || 0;
  const percentChange = pricedEquity?.[EquityFieldKey.dailyChange] ?? 0;

  return (
    <>
      <Stack
        sx={{
          position: 'relative',
        }}
      >
        {pricedEquity && (
          <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
            <IconButton
              size="small"
              onClick={() => setOpenModal(true)}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.2),
                },
              }}
            >
              <EditIcon fontSize="small" color="primary" />
            </IconButton>
          </Box>
        )}

        <Stack spacing={3}>
          <Stack sx={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
            <Typography variant="h6" color="text.primary">
              {sym}
            </Typography>
            {pricedEquity && (
              <Typography variant="body2" color="text.secondary">
                {pricedEquity.name}
              </Typography>
            )}
          </Stack>

          {pricedEquity && (
            <>
              <Stack direction="row" alignItems="baseline" spacing={3}>
                <Typography fontWeight="bold">
                  {formatAsCurrency(pricedEquity.price)}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography
                    variant="body1"
                    color={isPriceUp ? 'success.main' : 'error.main'}
                    fontWeight="medium"
                  >
                    {formatAsCurrency(priceChange)}
                  </Typography>
                  <Typography
                    variant="body1"
                    color={isPriceUp ? 'success.main' : 'error.main'}
                    fontWeight="medium"
                  >
                    ({formatAsPercentage(percentChange)})
                  </Typography>
                  {isPriceUp ? (
                    <ArrowUpwardIcon color="success" fontSize="small" />
                  ) : (
                    <ArrowDownwardIcon color="error" fontSize="small" />
                  )}
                </Stack>
              </Stack>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={displayedFields}
                  strategy={horizontalListSortingStrategy}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      mt: 1,
                      gap: 2,
                    }}
                  >
                    {displayedFields.map((field) => (
                      <TickerPill
                        key={field}
                        id={field}
                        field={field}
                        value={pricedEquity?.[field as keyof LegacyEquity]}
                        onDelete={() => handleRemoveField(field)}
                      />
                    ))}
                  </Box>
                </SortableContext>
                <DragOverlay>
                  {activeId ? (
                    <Chip
                      label={
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography variant="caption" color="text.secondary">
                            {
                              EQUITY_FIELD_LABEL_MAPPING[
                                activeId as EquityFieldKey
                              ]
                            }
                            :
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {formatEquityFieldValue(
                              activeId as EquityFieldKey,
                              pricedEquity?.[activeId as keyof LegacyEquity],
                            )}
                          </Typography>
                        </Stack>
                      }
                      onDelete={() => {}}
                      deleteIcon={<CloseIcon fontSize="small" />}
                      sx={{
                        m: 0.5,
                        p: 0.5,
                        height: 'auto',
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                      }}
                    />
                  ) : null}
                </DragOverlay>
              </DndContext>

              <Dialog open={openModal} onClose={handleCloseModal}>
                <DialogTitle>Edit Displayed Fields</DialogTitle>
                <DialogContent>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Add Fields
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {hiddenFields.map((field) => (
                        <Chip
                          key={field}
                          label={EQUITY_FIELD_LABEL_MAPPING[field]}
                          onClick={() => handleAddField(field)}
                          color="primary"
                          variant="outlined"
                          size="small"
                        />
                      ))}
                    </Box>
                  </Box>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleCloseModal}>Close</Button>
                </DialogActions>
              </Dialog>
            </>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default TickerOverview;
